/**
 * components/PageContainer
 * ──────────────────────────────────────────────────────────────────────────
 * The scrolling page body: the design's `26px 32px 52px` padding and a centred
 * max-width that varies by screen family — 1240 default, 1400 for the register,
 * 1040 for Settings and Settlement, 900 for the approver console, 720 for
 * onboarding.
 *
 * Both values come from lib/responsive, so a phone gets 16px padding and the
 * full width without any screen knowing it happened.
 * ──────────────────────────────────────────────────────────────────────────
 */
import type { ReactNode } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { neutral } from '../../theme/colors';
import type { contentWidth } from '../../theme/spacing';
import { useResponsive } from '../../lib/responsive';

export interface PageContainerProps {
  children: ReactNode;
  /** Which max-width family this screen belongs to. */
  width?: keyof typeof contentWidth;
  /** Onboarding uses the taller `44px 32px 60px` padding. */
  onboarding?: boolean;
  testID?: string;
}

export function PageContainer({
  children,
  width = 'default',
  onboarding = false,
  testID,
}: PageContainerProps) {
  const { pad, padTop, padBottom, contentMax } = useResponsive();

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={{
        paddingTop: onboarding ? padTop + 18 : padTop,
        paddingHorizontal: pad,
        paddingBottom: onboarding ? padBottom + 8 : padBottom,
      }}
      testID={testID}
    >
      {/* `alignSelf: center` with a maxWidth is the RN equivalent of
          `max-width: N; margin: 0 auto`. */}
      <View style={[styles.inner, { maxWidth: contentMax(width) }]}>{children}</View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1, backgroundColor: neutral.surface },
  inner: { width: '100%', alignSelf: 'center' },
});

export default PageContainer;
